<template>
  <div class="min-h-screen flex flex-col items-center py-10 px-4">
    <!-- Header -->
     <div class="mobile-padding"></div>
    <div class="liquidGlassCard rounded-3xl p-4 w-full relative">
      <button class="absolute w-8 h-8 left-5 top-5 " @click="$router.back">
        <ColorImg
          src="/images/icons/back.png"
          class="bg-current w-full h-full"
          alt=""
        />
      </button>
      <div class="absolute left-16 text-2xl top-5 title-color ">
        Performances
      </div>

      <PerformanceStatsChart tabToRight />
    </div>

    
    <!-- Challenges Section -->
    <div class="liquidGlassCard rounded-3xl p-4 w-full mt-6">
      <LevelBox class="p-2" />
      <div class="text-xl title-color mt-8 mb-2 px-2">Challenges</div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="(challenge, index) in challenges"
          :key="index"
          class="rounded-2xl border p-5 border-gray-200 bg-white flex flex-col justify-between"
        >
          <div>
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-lg font-semibold title-color">
                {{ challenge.name }}
              </h3>

              <!-- Badge du statut -->
              <div
                :class="[
                  'text- px-2 py-1 rounded-xl font-medium',
                  challenge.status === 'active'
                    ? 'bg-green-100 text-green-700  '
                    : challenge.status === 'pending'
                      ? 'bg-gray-100 text-gray-700  '
                      : 'bg-blue-100 text-blue-700  ',
                ]"
              >
                {{ getStatusLabel(challenge.status) }}
              </div>
            </div>

            <div
              class="text-gray-500 text-sm mb-2"
              v-if="challenge.status !== 'pending'"
            >
              Début : <span class="font-medium">{{ challenge.startDate }}</span>
            </div>

            <!-- Si le challenge est actif -->
            <div v-if="challenge.status === 'active'">
              <div
                class="flex items-center justify-between text-sm text-gray-700"
              >
                <span>Jours restants :</span>
                <span class="font-semibold text-gray-900">
                  {{ challenge.daysRemaining }}
                </span>
              </div>

              <div
                class="flex items-center justify-between text-sm text-gray-700 mt-1"
              >
                <span>Livraisons restantes :</span>
                <span class="font-semibold text-gray-900">
                  {{ challenge.validationsRemaining }}
                </span>
              </div>

              <!-- Barre de progression -->
              <div class="w-full h-2 bg-gray-200 rounded-full mt-3">
                <div
                  class="h-2 rounded-full transition-all duration-300"
                  :style="{
                    width: getProgress(challenge.validationsRemaining) + '%',
                    backgroundColor: getColor(challenge.validationsRemaining),
                  }"
                ></div>
              </div>
            </div>

            <!-- Si le challenge n'est pas encore commencé -->
            <div v-else-if="challenge.status === 'pending'">
              <div
                class="flex items-center justify-between text-sm text-gray-700 mt-1"
              >
                <span>Durée du challenge :</span>
                <span class="font-semibold text-gray-900">
                  {{ challenge.totalDays }}
                </span>
              </div>
              <div
                class="flex items-center justify-between text-sm text-gray-700 mt-1"
              >
                <span>Nombre de livraison pour terminer :</span>
                <span class="font-semibold text-gray-900">
                  {{ challenge.totalValidations }}
                </span>
              </div>
              <div class="mt-4 flex items-center justify-between">
                <!-- <div class="title-color text-lg"></div> -->
                <button
                  class="buttonComponent small flex gap-2 items-center"
                  @click="startChallenge(index)"
                >
                  <div class="w-5 h-5">
                    <ColorImg
                      src="/images/icons/lock.png"
                      class="bg-current w-full h-full"
                      alt=""
                    />
                  </div>
                  <div>Dévérouiller</div>
                </button>
              </div>
            </div>

            <!-- Si le challenge est terminé -->
            <div v-else>
              <div
                class="flex items-center justify-between text-sm text-gray-700 mt-1"
              >
                <span>Nombre de livraisons :</span>
                <span class="font-semibold text-gray-900">
                  {{ challenge.totalValidations }}
                </span>
              </div>
              <div class="mt-4 text-sm text-green-600 font-medium">
                ✅ Challenge terminé avec succès !
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import LevelBox from '../sections/commmons/LevelBox.vue'
import PerformanceStatsChart from '../sections/performance/PerformanceStatsChart.vue'
import { ref } from 'vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'

// === Données ===
// Seuls 2 challenges sont actifs, les autres sont "pending"
const challenges = ref([
  {
    name: 'Marathon du week-end',
    startDate: '2025-10-20',
    daysRemaining: 3,
    totalDays: 7,
    validationsRemaining: 5,
    totalValidations: 20,
    status: 'active',
  },
  {
    name: 'Objectif fidélité',
    startDate: '2025-10-01',
    daysRemaining: 8,
    totalDays: 14,
    validationsRemaining: 3,
    totalValidations: 10,
    status: 'pending',
  },
  {
    name: 'Livraisons du matin',
    startDate: '2025-10-21',
    daysRemaining: 2,
    totalDays: 5,
    validationsRemaining: 8,
    totalValidations: 15,
    status: 'pending',
  },
  {
    name: 'Nuit des rapides',
    startDate: '2025-10-22',
    daysRemaining: 1,
    totalDays: 3,
    validationsRemaining: 2,
    totalValidations: 8,
    status: 'pending',
  },
  {
    name: 'Champion de la semaine',
    startDate: '2025-10-14',
    daysRemaining: 4,
    totalDays: 7,
    validationsRemaining: 10,
    totalValidations: 18,
    status: 'pending',
  },
  {
    name: 'Objectif 100 km',
    startDate: '2025-10-10',
    daysRemaining: 6,
    totalDays: 10,
    validationsRemaining: 7,
    totalValidations: 20,
    status: 'completed',
  },
  {
    name: 'Top Rider du mois',
    startDate: '2025-10-01',
    daysRemaining: 9,
    totalDays: 30,
    validationsRemaining: 20,
    totalValidations: 40,
    status: 'pending',
  },
])
challenges.value=[]

// === Méthodes ===
function getColor(validationsRemaining) {
  if (validationsRemaining <= 3) return '#22c55e'
  if (validationsRemaining <= 7) return '#f59e0b'
  return '#ef4444'
}

function getProgress(validationsRemaining) {
  const max = 20
  const done = Math.max(0, max - validationsRemaining)
  return (done / max) * 100
}

function getStatusLabel(status) {
  switch (status) {
    case 'active':
      return 'En cours'
    case 'pending':
      return 'Vérouillé'
    case 'completed':
      return 'Validé'
    default:
      return ''
  }
}

// Démarre un challenge
function startChallenge(index) {
  const activeCount = challenges.value.filter(c => c.status === 'active').length
  if (activeCount >= 1) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Challenge déjà en cours',
      body: "Vous ne pouvez avoir qu'un challenges actif à la fois.",
    })
    return
  }
  challenges.value[index].status = 'active'
}
</script>
